import { Button, type Props as ButtonProps } from "@ui/Button";
import { Dropdown } from "@ui/Dropdown";
import { MaterialSymbol } from "@ui/MaterialSymbol";
import { HStack } from "@ui/Stack";
import { Text } from "@ui/typography/Text";
import { Match, Show, Switch } from "solid-js";
import { viewerIsOwner, viewerIsSubscribed } from "../askPageAccess";
import {
	type AskPagePrice,
	type AskPageViewerStatus,
	askPageVars,
} from "../pageVars";
import { useAskPageUnsubscribe } from "../useAskPageUnsubscribe";
import { AskPageUnsubscribePanel } from "./AskPageUnsubscribePanel";
import "./AskPageUnsubscribePanel.css";

type Props = {
	onJoin?: () => void;
	joinBusy?: () => boolean;
};

const DURATION_LABEL: Record<AskPagePrice["type"], string> = {
	pass_24h: "24 hours",
	pass_7d: "7 days",
	subscription: "1 month",
};

export const subscribedLabel = (
	paid: boolean,
	type: AskPagePrice["type"] | null,
	subscriptionName: string,
): string => {
	if (!paid) {
		return "Subscribed · Free";
	}

	const prefix = type === "subscription" ? `${subscriptionName} · ` : "Pass · ";
	const duration = type ? DURATION_LABEL[type] : null;
	return duration ? `${prefix}${duration}` : "Subscribed";
};

const isPassEntitlement = (type: AskPagePrice["type"] | null) =>
	type === "pass_24h" || type === "pass_7d";

const canManageSubscription = (
	status: AskPageViewerStatus,
	entitlementType: AskPagePrice["type"] | null,
) =>
	!isPassEntitlement(entitlementType) &&
	status !== "pass" &&
	(status === "free_subscriber" || status === "member");

const SubscribedStatusLabel = (props: {
	label: string;
	paidAskPage: boolean;
}) => {
	const textColour = () => (props.paidAskPage ? "purple" : "primary");
	const textSymbol = () =>
		props.paidAskPage ? "workspace_premium" : "check_circle";
	return (
		<HStack spacing="small" align="center">
			<MaterialSymbol
				symbol={textSymbol()}
				color={textColour()}
				relativeSize={0.75}
			/>
			<Text color={textColour()} weight="bold" family="mono" size="xsmall">
				{props.label}
			</Text>
		</HStack>
	);
};

const SubscribedTrigger = (props: {
	label: string;
	buttonProps: ButtonProps;
}) => (
	<span
		class="gvtui-button"
		classList={{
			[props.buttonProps.variant ?? "secondary"]: true,
			...(props.buttonProps.theme ? { [props.buttonProps.theme]: true } : {}),
		}}
	>
		<div>
			<Show when={props.buttonProps.symbol}>
				{(symbol) => (
					<MaterialSymbol
						symbol={
							Array.isArray(symbol()) ? symbol()[0] : (symbol() as string)
						}
						relativeSize={1.14}
					/>
				)}
			</Show>
			{props.label}
		</div>
	</span>
);

export const JoinButton = (props: Props) => {
	const vars = askPageVars();
	const {
		unsubscribe,
		busy: unsubBusy,
		error: unsubError,
	} = useAskPageUnsubscribe(vars.influencer_handle);

	const subscriptionName =
		vars.prices.find((price) => price.type === "subscription")
			?.subscription_name ?? "Subscription";

	const statusLabel = () =>
		subscribedLabel(
			vars.is_paid_ask_page,
			vars.entitlement_type,
			subscriptionName,
		);

	const subscribeOptions = (): { label: string; props: ButtonProps } => {
		const result: { label: string; props: ButtonProps } = {
			label: "",
			props: {},
		};
		if (vars.is_paid_ask_page) {
			result.label = "Subscribe";
			result.props = {
				theme: "purple",
				symbol: "add",
			};
		} else {
			result.label = "Subscribe · Free";
			result.props = {
				symbol: "add",
			};
		}
		return result;
	};

	const subscribedButtonProps = (): ButtonProps => {
		if (vars.is_paid_ask_page) {
			return {
				variant: "secondary",
				theme: "purple",
				symbol: "check",
			};
		}
		return {
			variant: "secondary",
			symbol: "check",
		};
	};

	const manageBusy = () => Boolean(props.joinBusy?.() || unsubBusy());

	return (
		<Show
			when={!viewerIsOwner(vars.status)}
			fallback={
				<Button
					as="a"
					href="/studio/my-ask-page"
					variant="primary"
					symbol="edit"
				>
					Edit page
				</Button>
			}
		>
			<Switch>
				<Match when={viewerIsSubscribed(vars.status)}>
					<Show
						when={canManageSubscription(vars.status, vars.entitlement_type)}
						fallback={
							<SubscribedStatusLabel
								label={statusLabel()}
								paidAskPage={vars.is_paid_ask_page}
							/>
						}
					>
						<Dropdown
							class="ask-page-unsubscribe-popover"
							placement="bottom end"
							disabled={manageBusy()}
							aria-label="Manage subscription"
							triggerVariant="none"
							listBoxStyle={{
								width: "min(22rem, calc(100vw - 2rem))",
							}}
							trigger={
								<SubscribedTrigger
									label={statusLabel()}
									buttonProps={{
										size: "large",
										...subscribedButtonProps(),
									}}
								/>
							}
							content={({ close, isOpen }) => (
								<AskPageUnsubscribePanel
									isOpen={isOpen}
									onClose={close}
									creatorName={vars.influencer_display_name}
									creatorAvatarUrl={vars.influencer_avatar_url}
									isPaidAskPage={vars.is_paid_ask_page}
									entitlementType={vars.entitlement_type}
									influencerHandle={vars.influencer_handle}
									onConfirm={() => unsubscribe().then(() => close())}
									busy={unsubBusy}
									error={unsubError}
								/>
							)}
						/>
					</Show>
				</Match>
				<Match
					when={
						!viewerIsSubscribed(vars.status) &&
						(vars.has_enabled_source || !vars.is_paid_ask_page)
					}
				>
					<Button
						type="button"
						variant="primary"
						{...subscribeOptions().props}
						disabled={props.joinBusy?.()}
						onClick={props.onJoin}
					>
						{subscribeOptions().label}
					</Button>
				</Match>
			</Switch>
		</Show>
	);
};
